import React, { useState } from "react";
import { Pressable } from "react-native";
import { useUnistyles, UnistylesRuntime } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Image } from "@src/components";
import type { TimelineEntry, SimilarFields } from "@visioni/a3s-vehicle-tracker";
import { TimelineCard } from "./TimelineCard";
import { buildCardTitle, isKnown } from "./card-utils";
import { formatTime } from "@visioni/a3s-vehicle-tracker/app";

interface GroupCardProps {
  group: Extract<TimelineEntry, { kind: "group" }>["group"];
  expandedEventId: string | null;
  onToggleExpanded: (id: string) => void;
  activeTimezone: string;
  actions: {
    onFindSimilar: (fields: SimilarFields) => void;
  };
}

function deriveGroupPlates(members: GroupCardProps["group"]["members"]): string | null {
  const plates = [...new Set(members.map((m) => m.plateText).filter((p) => isKnown(p)))];
  if (plates.length === 0) return null;
  return plates.join(" / ");
}

export function GroupCard({ group, expandedEventId, onToggleExpanded, activeTimezone, actions }: GroupCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { theme } = useUnistyles();
  const breakpoint = (UnistylesRuntime.breakpoint as string) ?? "md";
  const isSmall = breakpoint === "xs" || breakpoint === "sm" || breakpoint === "md";
  const thumbWidth = isSmall ? 72 : 98;

  const { members } = group;
  const firstSeenIso = members.reduce(
    (earliest, m) => (m.firstSeenAt < earliest ? m.firstSeenAt : earliest),
    members[0]!.firstSeenAt,
  );
  const lastSeenIso = members.reduce(
    (latest, m) => (m.lastSeenAt > latest ? m.lastSeenAt : latest),
    members[0]!.lastSeenAt,
  );
  const cameraCount = new Set(members.map((v) => v.cameraId)).size;
  const firstImageUrl = members[0]!.imageUrls[0];
  const title = buildCardTitle(members[0]!);
  const plates = deriveGroupPlates(members);

  return (
    <View mb={expanded ? undefined : "xl"} style={{ overflow: "visible" } as Record<string, string>}>
      <Pressable onPress={() => setExpanded((prev) => !prev)}>
        <View
          bg="surface"
          borderRadius={12}
          borderWidth={1}
          borderColor="border"
          flexDirection="row"
          alignItems="stretch"
          style={{ minHeight: 88 }}
        >
          {firstImageUrl && (
            <View style={{ width: thumbWidth, flexShrink: 0 }}>
              <Image
                source={{ uri: firstImageUrl }}
                style={{ width: "100%", height: "100%", borderRadius: 11 }}
                contentFit="cover"
              />
            </View>
          )}
          <View flex={1} style={{ padding: 8 }} flexDirection="row" alignItems="center" gap="sm">
            <View flex={1}>
              <View flexDirection="row" alignItems="center" gap="sm">
                <Text textStyle="md/semibold" color="text" numberOfLines={1}>
                  {title}
                </Text>
                <View
                  style={{
                    paddingHorizontal: 6,
                    paddingVertical: 2,
                    borderRadius: 10,
                    backgroundColor: theme.colors.muted,
                  }}
                >
                  <Text textStyle="xxs/semibold" color="text">
                    {members.length}
                  </Text>
                </View>
              </View>
              {plates && (
                <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                  {plates}
                </Text>
              )}
              <Text textStyle="xs/regular" color="textMuted">
                {cameraCount} camera{cameraCount !== 1 ? "s" : ""} · {formatTime(firstSeenIso, activeTimezone)} –{" "}
                {formatTime(lastSeenIso, activeTimezone)}
              </Text>
            </View>
            <Text textStyle="sm/regular" color="textMuted">
              {expanded ? "▾" : "▸"}
            </Text>
          </View>
        </View>
      </Pressable>

      {expanded &&
        members.map((vehicle, idx) => (
          <View
            key={vehicle.id ?? `${vehicle.createdAt}-${idx}`}
            style={
              {
                marginLeft: isSmall ? Math.round((thumbWidth + 8) / 3) : thumbWidth + 8,
                marginTop: idx === 0 ? 12 : 0,
                zIndex: members.length - idx,
                overflow: "visible",
              } as Record<string, unknown>
            }
          >
            <TimelineCard
              vehicle={vehicle}
              expandedEventId={expandedEventId}
              onToggleExpanded={onToggleExpanded}
              activeTimezone={activeTimezone}
              actions={actions}
            />
          </View>
        ))}
    </View>
  );
}
